import { useCallback, useRef, useState } from 'react';

const getVisibleSlidesCount = swiper => {
  if (typeof swiper?.slidesPerViewDynamic !== 'function') {
    return 1;
  }

  try {
    const visibleSlidesCount = swiper.slidesPerViewDynamic();

    return Number.isFinite(visibleSlidesCount) && visibleSlidesCount > 0 ? visibleSlidesCount : 1;
  } catch {
    return 1;
  }
};

const useBonusSliderAtEnd = resetKey => {
  const [atEnd, setAtEnd] = useState(false);
  const swiperRef = useRef(null);
  const pendingSlideRequestRef = useRef(null);
  const resetKeyRef = useRef(resetKey);

  if (resetKeyRef.current !== resetKey) {
    resetKeyRef.current = resetKey;
    swiperRef.current = null;
    pendingSlideRequestRef.current = null;
  }

  const scrollToSlide = useCallback((index, { force = false } = {}) => {
    if (index == null || index < 0) {
      pendingSlideRequestRef.current = null;
      return;
    }

    const swiper = swiperRef.current;

    if (!swiper) {
      pendingSlideRequestRef.current = { force, index };
      return;
    }

    pendingSlideRequestRef.current = null;

    const visibleSlidesCount = getVisibleSlidesCount(swiper);
    const firstVisibleIndex = swiper.activeIndex ?? 0;
    const lastVisibleIndex = firstVisibleIndex + Math.max(visibleSlidesCount - 1, 0);

    if (!force && index >= firstVisibleIndex && index <= lastVisibleIndex) {
      return;
    }

    swiper.slideTo?.(index, 0, false);
  }, []);

  const onSwiper = useCallback(
    swiper => {
      if (!swiper) {
        return;
      }

      swiperRef.current = swiper;

      const sync = () => {
        setAtEnd(Boolean(swiper.isEnd));
      };

      sync();
      swiper.on('resize', sync);
      swiper.on('slideChange', sync);
      swiper.on('slideChangeTransitionEnd', sync);

      if (pendingSlideRequestRef.current != null) {
        const { force, index } = pendingSlideRequestRef.current;
        pendingSlideRequestRef.current = null;
        scrollToSlide(index, { force });
      }
    },
    [scrollToSlide],
  );

  return { atEnd, onSwiper, scrollToSlide };
};

export default useBonusSliderAtEnd;
